import { Skeleton } from "@/components/_components/skeleton";
import { Card, CardContent } from "@/components/_components/card";
import { Button } from "@/components/_components/button";
import * as api from "@/lib/api/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/_components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import toast from "react-hot-toast";
import { useState, useEffect, useRef } from "react";
import { FileText, Search, RotateCcw, Edit, BarChart3, Plus } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { usePageHeader } from "@/contexts/page-header-context";
import { Badge } from "@/components/_components/badge";
import { AGENT_TYPE, AGENT_TYPE_LABELS } from "@/constants/constants";
import type { ConstantMessageDto } from "@/lib/api/types";
import UpdateConstantMessageDialog from "./_components/UpdateConstantMessageDialog";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { useErrorHandler } from "@/hooks/useErrorHandler";

interface Country {
  id: number;
  countryCode: string;
  countryName: string;
  language?: string;
  isdCode: string;
  startWorkingHour: string;
  endWorkingHour: string;
}

const ConstantsManagementPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_CONSTANTS_MGMT_VIEW">
      <ConstantsManagementContent />
    </PageAccessGuard>
  );
};

const ConstantsManagementContent: React.FC = () => {
  const { setHeaderData } = usePageHeader();
  const { handleError } = useErrorHandler();
  const [constants, setConstants] = useState<ConstantMessageDto[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedCountriesRef = useRef(false);
  const isInitialMountRef = useRef(true);

  const [searchFilter, setSearchFilter] = useState({
    agentType: "",
    countryCode: "USA",
  });

  const fetchCountries = async () => {
    try {
      setIsLoadingCountries(true);
      const response = await api.getCountriesList();
      setCountries(response.data || []);
    } catch (err: unknown) {
      handleError(err);
    } finally {
      setIsLoadingCountries(false);
    }
  };

  const fetchConstants = async () => {
    if (!searchFilter.agentType || !searchFilter.countryCode) {
      toast.error("Please select both Agent Type and Country");
      return;
    }

    const selectedCountry = countries.find(c => c.countryCode === searchFilter.countryCode);
    if (!selectedCountry || !selectedCountry.language) {
      toast.error("Selected country does not have a language configured");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getConstantMessages(
        searchFilter.agentType,
        selectedCountry.language
      );
      setConstants(response.data || []);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch constants";
      setError(errorMessage);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetchedCountriesRef.current) {
      hasFetchedCountriesRef.current = true;
      fetchCountries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchConstants();
  };

  const handleReset = () => {
    setSearchFilter({
      agentType: "",
      countryCode: "USA",
    });
    setConstants([]);
    toast.success("Form reset successfully");
  };

  // Auto-search when agent type or country changes (skip initial mount)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    if (searchFilter.agentType && searchFilter.countryCode) {
      fetchConstants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter.agentType, searchFilter.countryCode]);

  const handleUpdateSuccess = () => {
    fetchConstants();
  };

  // Set page header data
  useEffect(() => {
    setHeaderData({
      stats: constants.length > 0 ? {
        count: constants.length,
        label: 'constant',
        icon: <BarChart3 className="h-4 w-4" />
      } : undefined,
    });

    return () => {
      setHeaderData(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constants.length]);

  const selectedCountry = countries.find(c => c.countryCode === searchFilter.countryCode);
  const isEnglish = selectedCountry?.language?.toLowerCase() === 'english';

  if (isLoadingCountries) {
    return (
      <div className="bg-background">
        <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-2 py-4">
          <div className="space-y-4">
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background">
      <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">
          {/* Search & Filter Section */}
          <div className="bg-card border border-border/50 rounded-lg mb-2">
            <div className="px-4 pt-3 pb-2 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/10 rounded-md">
                  <Search className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    Search & Filter
                  </h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">
                    Select agent type and country to view constant messages
                  </p>
                </div>
              </div>
            </div>

            <div className="px-4 pb-4 pt-4">
              <div className="flex items-end gap-3 flex-wrap">
                {/* Agent Type */}
                <div className="space-y-1.5 flex-1 min-w-[200px]">
                  <label htmlFor="agentType" className="block text-sm font-semibold text-foreground">
                    Agent Type <span className="text-destructive">*</span>
                  </label>
                  <Select
                    value={searchFilter.agentType}
                    onValueChange={(value) =>
                      setSearchFilter((prev) => ({ ...prev, agentType: value }))
                    }
                  >
                    <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder="Select agent type" />
                    </SelectTrigger>
                    <SelectContent>
                      {AGENT_TYPE.map((type) => (
                        <SelectItem key={type} value={type}>
                          {AGENT_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Country */}
                <div className="space-y-1.5 flex-1 min-w-[200px]">
                  <label htmlFor="country" className="block text-sm font-semibold text-foreground">
                    Country <span className="text-destructive">*</span>
                  </label>
                  <Select
                    value={searchFilter.countryCode}
                    onValueChange={(value) =>
                      setSearchFilter((prev) => ({ ...prev, countryCode: value }))
                    }
                  >
                    <SelectTrigger className="h-9 bg-background border border-border hover:border-primary/60 transition-colors focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {countries.map((country) => (
                        <SelectItem key={country.countryCode} value={country.countryCode}>
                          {country.countryName} - {country.language || 'N/A'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSearch}
                    disabled={isLoading || !searchFilter.agentType || !searchFilter.countryCode}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    {isLoading ? (
                      <>
                        <Search className="h-4 w-4 mr-1.5 animate-pulse" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-1.5" />
                        Search
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleReset}
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    <RotateCcw className="h-4 w-4 mr-1.5" />
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          {error ? (
            <Card className="shadow-md border-destructive/50 bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 mx-auto mb-4 bg-destructive/10 rounded-full flex items-center justify-center">
                    <FileText className="w-8 h-8 text-destructive" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Error loading data
                  </h3>
                  <p className="text-muted-foreground mb-4 text-sm">{error}</p>
                  <Button onClick={fetchConstants} variant="outline">
                    Try Again
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : constants.length === 0 ? (
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                    <Search className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Constants Found</h3>
                  <p className="text-muted-foreground mb-4">
                    Select agent type and country, then click Search to view constant messages.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-border/50 bg-card backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h2 className="text-xl font-bold text-foreground">
                      Constant Messages
                    </h2>
                    <Badge variant="secondary" className="text-xs font-medium">
                      {constants.length} {constants.length === 1 ? 'record' : 'records'}
                    </Badge>
                    {!isEnglish && (
                      <Badge variant="outline" className="text-xs font-medium text-amber-600 border-amber-600">
                        Read-only (Non-English)
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="w-full">
                  <div className="overflow-hidden rounded-lg border border-border/50">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
                        <TableRow>
                          <TableHead className="font-semibold">Sl No</TableHead>
                          <TableHead className="font-semibold">Agent Type</TableHead>
                          <TableHead className="font-semibold">Message Key</TableHead>
                          <TableHead className="font-semibold">Channel</TableHead>
                          <TableHead className="font-semibold">Message Value</TableHead>
                          <TableHead className="font-semibold">Message Desc</TableHead>
                          <TableHead className="font-semibold">Last Updated By</TableHead>
                          <TableHead className="font-semibold">Last Update At</TableHead>
                          {isEnglish && (
                            <TableHead className="font-semibold text-center">Actions</TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {constants.map((constant, index) => (
                          <TableRow
                            key={constant.id}
                            className="transition-colors hover:bg-muted/50"
                          >
                            <TableCell className="font-medium">{index + 1}</TableCell>
                            <TableCell>
                              {AGENT_TYPE_LABELS[constant.agentType as keyof typeof AGENT_TYPE_LABELS] || constant.agentType}
                            </TableCell>
                            <TableCell className="font-mono text-sm">{constant.messageKey}</TableCell>
                            <TableCell>
                              {constant.channel || '-'}
                            </TableCell>
                            <TableCell className="max-w-md">
                              <div className="whitespace-pre-wrap break-words">
                                {constant.messageValue}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-md">
                              <div className="truncate" title={constant.messageDesc || ''}>
                                {constant.messageDesc || '-'}
                              </div>
                            </TableCell>
                            <TableCell>{constant.updatedBy || '-'}</TableCell>
                            <TableCell className="text-sm">
                              {constant.updatedAt ? formatDateTime(constant.updatedAt) : '-'}
                            </TableCell>
                            {isEnglish && (
                              <TableCell>
                                <div className="flex gap-1 justify-center">
                                  {!constant.channel && (
                                    <UpdateConstantMessageDialog
                                      constant={constant}
                                      onSuccess={handleUpdateSuccess}
                                      mode="create"
                                      trigger={
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600 transition-colors"
                                          title="Add Channel"
                                        >
                                          <Plus className="h-4 w-4" />
                                        </Button>
                                      }
                                    />
                                  )}
                                  <UpdateConstantMessageDialog
                                    constant={constant}
                                    onSuccess={handleUpdateSuccess}
                                    mode="edit"
                                    trigger={
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                                        title="Edit Constant"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                    }
                                  />
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConstantsManagementPage;
